import React, { useReducer, useState } from 'react';
import classnames from 'classnames';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';

import Translate, { useI18n } from '@hydra/common/components/i18n';
import Screenshot from '@hydra/common/components/Screenshot';
import Icon from '@hydra/common/components/Icon';
import ScoreCircle from '@hydra/common/components/ScoreCircle';
import ProgressBar from '@hydra/common/components/ProgressBar';
import Dot from '@hydra/common/components/Dot';
import ReviewDashboardFooter from '@hydra/common/components/ReviewDashboard/ReviewDashboardFooter';
import BannerOutdatedCrits from '@hydra/common/components/ReviewDashboard/BannerOutdatedCrits';
import DialogOutdatedCrits from '@hydra/common/components/ReviewDashboard/DialogOutdatedCrits';

import { CRITERIUM_STATUS } from '@hydra/utils/criterium';
import { useReviewContext } from '@hydra/common/context/review';
import insidePageRibbon from '@hydra/assets/img/review/internal-ribbon.svg';
import { getCompetitorColor } from '@hydra/utils/colors';
import useSkipSsr from '@hydra/common/customHooks/useSkipSsr';
import { GenerationProvider } from '@hydra/common/components/GenerationContext';
import { generationReducer, initialGenerationState } from '@hydra/common/components/generationReducer';
import { isHomepage } from '@hydra/utils/urlUtil';
import { useSection } from '@hydra/common/components/ScrollSpy';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(6.5),
    marginTop: theme.spacing(-4)
  },
  module: {
    padding: theme.spacing(2.5, 0, 1.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius * 2.5
  },
  screenshot: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block'
    }
  },
  iconText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[500],
    fontSize: theme.typography.fontSizeSmall,
    width: 90,
    display: 'inline-block',
    verticalAlign: 'top'
  },
  progressBar: {
    width: 150,
    display: 'inline-block',
    overflow: 'visible',
    verticalAlign: 'super'
  },
  inner: {
    display: 'flex',
    padding: theme.spacing(0, 2),
    flexDirection: 'column-reverse',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'flex-start'
    }
  },
  scoreCircle: {
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      marginLeft: 'auto',
      marginBottom: 0
    }
  },
  title: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightSemibold,
    textDecoration: 'none',
    fontSize: theme.typography.fontSizeLarge,
    marginBottom: theme.spacing(1),
    display: '-webkit-box',
    boxOrient: 'vertical',
    lineClamp: 4,
    wordBreak: 'break-all',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
      textAlign: 'left'
    }
  },
  updatedAtText: {
    color: theme.palette.grey[500],
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.fontSizeSmall
  },
  center: {
    marginLeft: 0,
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(4)
    }
  },
  tooltip: {
    fontSize: theme.typography.fontSizeSmall,
    lineHeight: 1.25,
    padding: theme.spacing(1)
  },
  progressContainer: { marginBottom: theme.spacing(1) },
  status: { paddingTop: theme.spacing(2) },
  insidePage: {
    position: 'absolute',
    display: 'none',
    width: 68,
    height: 68,
    top: 0,
    left: 152,
    [theme.breakpoints.up('sm')]: {
      display: 'block'
    }
  },
  thumbLoader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1
  },
  placeholderWrapper: {
    position: 'relative'
  },
  compStyles: {
    color: theme.palette.grey[400],
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightRegular
  },
  score: {
    display: 'inline-block',
    margin: theme.spacing(0, 2, 0, 2)
  },
  url: {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    maxWidth: 200,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  competitorContainer: {
    paddingTop: theme.spacing(2)
  },
  competitorLine: {
    marginBottom: theme.spacing(1)
  },
  dot: { verticalAlign: 'baseline' }
}));

const convertStatus = status => status.replace('Percent', '');

const getProgressBarColor = ({ color, theme }) => {
  if (color === 'success') {
    return theme.palette.success.main;
  }
  return theme.palette[color].main;
};

export const ReviewDashboard = props => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { shouldComponentRender } = useSkipSsr();
  const { moment } = useI18n();
  const { isLeadgen, isAssistant, isDudaEditor } = useReviewContext();
  const ref = useSection('dashboard');

  const {
    url,
    competitors,
    screenshot,
    score,
    updatedAt,
    progress,
    pdfTemplates,
    outdatedCriteria = {}
  } = props;
  const hasOutdatedCriteria = Object.keys(outdatedCriteria).length > 0;
  const theme = useTheme();
  const classes = useStyles();
  const useGenerationState = useReducer(generationReducer, {
    ...initialGenerationState,
    url
  });
  const showDashboard = !(isAssistant && isDudaEditor);

  return (
    <div className={showDashboard && classes.root} id='dashboard' ref={ref}>
      {
        (!isLeadgen && hasOutdatedCriteria) && (
          <BannerOutdatedCrits handleOpen={handleOpen} />
        )
      }
      <DialogOutdatedCrits open={open} handleClose={handleClose} />
      {
        showDashboard && (
          <div className={classes.module}>
            <div className={classnames(classes.inner, 'nr-review-summary-header')}>
              <div className={classes.placeholderWrapper}>
                <Screenshot
                  type='desktop'
                  src={screenshot}
                  className={classes.screenshot}
                  width='202'
                  height='115'
                  alt=''
                />
                {!isHomepage(url) && (
                  <div
                    className={classes.insidePage}
                    style={{ background: `url(${insidePageRibbon}) top right no-repeat` }}
                  />
                )}
              </div>
              <div className={classes.center}>
                <Link
                  underline='none'
                  className={classes.title}
                  href={/^https?:\/\//.test(url) ? url : `http://${url}`}
                  rel='nofollow noreferrer noopener'
                  target='_blank'
                >
                  {url}
                </Link>
                <div className={classes.updatedAtText}>
                  {shouldComponentRender ? moment(updatedAt).format('LLL') : <div>&nbsp;</div>}
                </div>
                <div className={classes.status}>
                  {progress && Object.keys(progress).map(status => {
                    const { type, text, color } = CRITERIUM_STATUS[convertStatus(status)];
                    const progressBarColor = getProgressBarColor({ color, theme });
                    return (
                      <div key={type} className={classes.progressContainer}>
                        <Icon type={type} size='md' />
                        <Translate content={text} className={classes.iconText} />
                        <div className={classes.progressBar}>
                          <ProgressBar
                            max={1}
                            value={progress[status]}
                            color={progressBarColor}
                            borderColor={progressBarColor}
                            size='xs'
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {competitors && competitors.length > 0 && (
                  <div className={classes.competitorContainer}>
                    {competitors.map(({ url, score }, i) => (
                      <div key={i} className={classes.competitorLine}>
                        <Dot color={getCompetitorColor(theme.palette.competitor, i)} className={classes.dot} />
                        <div className={classnames(classes.score, classes.compStyles)}>
                          {Math.ceil(score)}
                        </div>
                        <div className={classnames(classes.url, classes.compStyles)}>
                          {url}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Tooltip
                placement='left'
                title={!isLeadgen ? <Translate content='tooltip_rank' className={classes.tooltip} /> : ''}
              >
                <div className={classes.scoreCircle}>
                  <ScoreCircle
                    showProgress
                    score={score}
                  />
                </div>
              </Tooltip>
            </div>
            {!(isLeadgen || isAssistant) && (
              <GenerationProvider value={useGenerationState}>
                <ReviewDashboardFooter
                  competitors={competitors}
                  pdfTemplates={pdfTemplates}
                />
              </GenerationProvider>
            )}
          </div>
        )
      }
    </div>
  );
};

export default React.memo(ReviewDashboard);
